import AppButton from '@nxsys/components/AppButton';
import {
  defaultCisStatus,
  defaultVatMethodOptions,
} from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { commonActions } from '@nxsys/store/slices/common/reducer/commonSlice';
import { UpdateCompanyByIdParams } from '@nxsys/types/api/company';
import { Icons } from '@nxsys/types/icons';
import { ICompany } from '@nxsys/types/models/company';
import { countryList } from '@nxsys/utils';
import { compactVatRegNo } from '@nxsys/utils/formFieldValidation';
import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CompanyDetailForm, {
  CompanyDetailFormRef,
  CompanyDetailFormValues,
} from './components/CompanyDetailForm';
import { useCompanyDetailFieldVisibility } from './components/useCompanyDetailFieldVisibility';

type Props = {
  data?: ICompany;
  updateData: (params: UpdateCompanyByIdParams) => Promise<void>;
};

const CompanyDetailTab: React.FC<Props> = ({ data, updateData }) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const { showCis, showVat, showVatMethod } = useCompanyDetailFieldVisibility(
    data?.paymentModel,
  );
  const dispatch = useDispatch<AppDispatch>();
  const formRef = useRef<CompanyDetailFormRef>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSubmit = useCallback(
    async (values: CompanyDetailFormValues) => {
      setIsUpdating(true);
      try {
        const params: UpdateCompanyByIdParams = {
          companyName: values.companyName || '',
          tradingName: values.tradingName || '',
          address: [
            values.address1 || '',
            values.address2 || '',
            values.address3 || '',
            values.address4 || '',
          ],
          city: values.city || '',
          county:
            typeof values.county === 'object'
              ? (values.county as any)?.value || ''
              : values.county || '',
          postCode: values.postCode || '',
          country: values.country?.value || '',
          companyNo: values.companyNo || '',
          incorporationDate: values.incorporationDate || null,
          UTRNo: values.UTRNo || '',
          // Only send what the form rendered: a hidden field has no value, and
          // a blank sent for it would overwrite the stored one.
          ...(showCis
            ? {
                cisVerificationNo: values.cisVerificationNo || '',
                cisVerificationDate: values.cisVerificationDate || null,
                cisStatus: values.cisStatus?.value || null,
              }
            : {}),
          ...(showVat
            ? {
                VATRegistered: values.VATRegistered || false,
                // The Irish field is pre-filled with a bare "IE", which is
                // no number.
                VATRegNo: compactVatRegNo(values.VATRegNo) ? values.VATRegNo : '',
                VATEffectiveDate: values.VATEffectiveDate || null,
              }
            : {}),
          ...(showVatMethod
            ? { VATMethod: values.VATMethod?.value || null }
            : {}),
        };

        const isUploadLogo = !!(values.logoUrl as File)?.name;
        if (isUploadLogo) {
          const res = await dispatch(
            commonActions.uploadFileToS3({ file: values.logoUrl as File }),
          ).unwrap();
          params.logoUrl = res.url;
        }

        const isUploadBanner = !!(values.bannerUrl as File)?.name;
        if (isUploadBanner) {
          const res = await dispatch(
            commonActions.uploadFileToS3({ file: values.bannerUrl as File }),
          ).unwrap();
          params.bannerUrl = res.url;
        }

        await updateData(params);
      } finally {
        setIsUpdating(false);
      }
    },
    [updateData, dispatch, showCis, showVat, showVatMethod],
  );

  return (
    <div>
      {data && (
        <>
        <CompanyDetailForm
          formRef={formRef}
          onSubmit={handleSubmit}
          paymentModel={data.paymentModel}
          defaultValues={{
            companyName: data.companyName ?? '',
            tradingName: data.tradingName ?? '',
            address1: data.address?.[0] ?? '',
            address2: data.address?.[1] ?? '',
            address3: data.address?.[2] ?? '',
            address4: data.address?.[3] ?? '',
            city: data.city ?? '',
            county: data.county ?? '',
            postCode: data.postCode ?? '',
            country:
              countryList.find((item) => item.value === data.country) ?? null,
            companyNo: data.companyNo ?? '',
            incorporationDate: data.incorporationDate ?? null,
            UTRNo: data.UTRNo ?? '',
            cisVerificationNo: data.cisVerificationNo ?? '',
            cisVerificationDate: data.cisVerificationDate ?? null,
            cisStatus:
              defaultCisStatus.find(
                (item) => item.value === data.cisStatus,
              ) ?? null,
            VATEffectiveDate: data.VATEffectiveDate ?? null,
            VATRegistered: data.VATRegistered ?? false,
            VATMethod:
              defaultVatMethodOptions.find(
                (item) => item.value === data.VATMethod,
              ) ?? null,
            VATRegNo: data.VATRegNo || (isIreland ? 'IE' : ''),
            logoUrl: data.logoUrl ?? null,
            bannerUrl: data.bannerUrl ?? null,
          }}
        />
        <div className="flex items-center justify-start gap-3 pt-4">
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle
                color={defaultTheme.theme.palette.white}
              />
            }
            onClick={() => formRef.current?.submit()}
            loading={isUpdating}
          />
        </div>
        </>
      )}
    </div>
  );
};

export default CompanyDetailTab;
